import React, { ChangeEventHandler, FC, MouseEventHandler, useCallback, useContext, useEffect, useMemo } from 'react'

import { withOptimizely, WithOptimizelyProps } from '@optimizely/react-sdk'
import { UserInfo } from '@optimizely/react-sdk/dist/utils'
import { useI18n } from '@shopify/react-i18n'
import {
    Badge,
    Box,
    ButtonV2,
    Card,
    CardBody,
    CardFooter,
    Column,
    Divider,
    Icon,
    IconType,
    Link,
    Pill,
    Row,
    Select,
    Text,
} from '@vividseats/vivid-ui-kit'
import classNames from 'classnames'
import dayjs from 'dayjs'
import cloneDeep from 'lodash/cloneDeep'
import isEqual from 'lodash/isEqual'
import router, { useRouter } from 'next/router'
import { v4 as uuidv4 } from 'uuid'

import useMarketplaceDisclaimer from '@/components/layouts/main-layout/hooks/use-marketplace-disclaimer'
import filterContext from '@/components/pages/production/components/context'
import ListingBadges from '@/components/pages/production/components/listing-badges'
import ticketDetailsContext from '@/components/pages/production/components/ticket-details/context'
import PaypalMessagesWrapper from '@/components/shared/paypal-messages-wrapper'
import { SECONDARY_MARKETPLACE_DISCLAIMER_TEXT } from '@/constants'
import { DEFAULT_CURRENCY } from '@/context/internationalization/constants'
import ListingContext from '@/context/listing'
import { PaypalProvider } from '@/context/paypal'
import productionDetailsContext from '@/context/production-details'
import type { ProductionVenueInfo } from '@/context/production-details/types'
import { useIsSuperBowl } from '@/hooks/use-is-super-bowl'
import { usePromoPrice } from '@/hooks/use-promo-price'
import { useAllInPricingByVenueState } from '@/optimizely/all-in-pricing-by-venue-state-feature/hooks/use-all-in-pricing-by-venue-state'
import { OPTIMIZELY_EVENT } from '@/optimizely/constants'
import { useAllInPricingConnecticutFeature } from '@/optimizely/features/all-in-pricing-connecticut-feature'
import { useAllInPricingTennesseeFeature } from '@/optimizely/features/all-in-pricing-tennessee-feature'
import { useGeneralAdmissionUKFeature } from '@/optimizely/features/internationalization/hooks/general-admission-uk'
import { usePaypalPayLaterFeature } from '@/optimizely/features/paypal-pay-later-feature/hooks'
import { ComposedListing, ListingsGroup } from '@/types/app-types'
import { getLastItemListName } from '@/utils/analytics'
import { getFormattedCurrency } from '@/utils/i18n'

import BuyerGuaranteeBox from '../buyer-guarantee-box'
import FaceValue from '../face-value/component'
import TicketInfoPromoBanner from '../ticket-info-promo-banner'
import TicketInfoSectionHeader from '../ticket-info-section-header/component'

import styles from './styles.module.scss'

type UserSetterWith<T> = T & { setUser?: (user: UserInfo | undefined) => void }

export interface TicketInformationProps extends UserSetterWith<Partial<WithOptimizelyProps>> {
    listing: ComposedListing
    listingGroup?: ListingsGroup
    isFaceValue: boolean
    price: number
    productionVenueInfo: ProductionVenueInfo
    showAllInPricing: boolean
    showAllTicketsInSection: boolean
}

const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
})

const footerPadding = {
    bottom: 0.75,
    top: 1,
}

const TicketInformation: FC<TicketInformationProps> = ({
    optimizely,
    productionVenueInfo,
    listing,
    listingGroup,
    showAllInPricing,
    showAllTicketsInSection,
    isFaceValue,
    price,
    setUser,
}) => {
    const [isPayLaterEnabled] = usePaypalPayLaterFeature()
    const { isSuperBowlProduction } = useIsSuperBowl()
    const [i18n] = useI18n()
    const [isGeneralAdmissionUKEnabled] = useGeneralAdmissionUKFeature()

    const {
        dispatch: { toggleDetails, updateQuantity },
    } = useContext(ticketDetailsContext)

    const {
        state: { data },
        selectors: { i18nListingData },
        dispatch: { refetch },
    } = useContext(ListingContext)

    const {
        state: filterState,
        dispatch: { onSectionSelectAction },
    } = useContext(filterContext)

    const { state: productionDetailsState } = useContext(productionDetailsContext)
    const { data: production } = productionDetailsState
    const { productionName, productionDateTime, venueName, venueAddress, productionId, countryCode, venueState } =
        productionVenueInfo

    const {
        isGreatDeal,
        isPreferredSeller,
        section,
        sectionLong,
        row,
        defaultQuantity: quantity,
        deliveryMethod,
        isInstantDelivery,
        faceValue,
        id: ticketId,
        isZoneSeating,
        sectionId,
        inGroup,
    } = listing

    const applyCurrencyConversion = !!i18nListingData?.applyCurrencyConversion
    const localPrices = data?.listing?.localPrices

    const description = data?.listing?.description
    const aipFee =
        applyCurrencyConversion && localPrices?.allInPrice ? localPrices.allInPrice : data?.listing?.allInPrice
    const serviceFee = (applyCurrencyConversion ? localPrices?.serviceCharge : data?.listing?.serviceCharge) ?? 0
    const ticketPrice = applyCurrencyConversion && localPrices?.price ? localPrices.price : price

    const showFaceValueForUK = isGeneralAdmissionUKEnabled && data?.listing.showFaceValue
    const showSellerInfo = isGeneralAdmissionUKEnabled && data?.listing.showSellerInfo

    const isUSVenue = countryCode.toLowerCase() === 'us'
    const inHandDate = data?.listing?.inHandDate
    const shouldShowDeliveryDate = isZoneSeating && inHandDate && ['NJ', 'TN', 'MD'].includes(venueState)

    /* istanbul ignore next */
    useEffect(() => {
        const user = cloneDeep(optimizely?.user)
        if (user?.attributes) {
            if (!isEqual(user, optimizely?.user)) {
                if (setUser) setUser(user)
            }
        }
    }, [listing.id, quantity])

    const { mobileDisclaimerTypes } = useMarketplaceDisclaimer()

    const mobileHasSecondaryMarketplaceDisclaimer: boolean = useMemo(
        () => mobileDisclaimerTypes.includes('secondaryMarketplace'),
        [mobileDisclaimerTypes],
    )

    const [isAllInPricingByVenueStateFeatureEnabled] = useAllInPricingByVenueState()
    const [isTennesseeAllInPricingEnabled] = useAllInPricingTennesseeFeature()
    const [isConnecticutAllInPricingEnabled] = useAllInPricingConnecticutFeature()

    const isAllInPricing =
        filterState.showAllInPrice ||
        isAllInPricingByVenueStateFeatureEnabled ||
        isTennesseeAllInPricingEnabled ||
        isConnecticutAllInPricingEnabled

    const getCheckoutHref = useCallback((): string => {
        const itemListName = getLastItemListName(productionId)
        const analyticsIdQuery = itemListName ? `&itemListName=${itemListName}` : ''
        return `/checkout?productionId=${productionId}&ticketId=${ticketId}&quantity=${quantity}&uuid=${uuidv4()}${analyticsIdQuery}${
            inGroup && !!listingGroup ? `&isRollup=true` : ''
        }`
    }, [quantity, productionId, ticketId, inGroup, listingGroup])

    const checkoutHref = useMemo(() => getCheckoutHref(), [getCheckoutHref])

    const onSelectChange: ChangeEventHandler<HTMLSelectElement> = (e) => {
        updateQuantity({ newQuantity: e.target.value })
    }

    useEffect(() => {
        if (filterState.quantityFilterState.quantity > 0)
            updateQuantity({ newQuantity: filterState.quantityFilterState.quantity.toString() })
    }, [filterState.quantityFilterState.quantity])

    const qtyDropdownValues = useMemo(() => {
        const quantities =
            listing.inGroup && !!listingGroup ? listingGroup.quantityOptionsList : listing.quantityOptionsList

        return quantities.map((qty) => ({
            value: qty,
            label: qty > 1 ? `${qty} tickets` : `${qty} ticket`,
            dataTestId: `quantity-option-${qty}`,
        }))
    }, [listing.inGroup, listing.quantityOptionsList, listingGroup])

    const {
        pathname,
        query: { slug, id },
    } = useRouter()

    const qtyDropdown = (
        <Select
            name="quantity-select"
            className={styles.select}
            size="lg"
            options={qtyDropdownValues}
            disabled={qtyDropdownValues.length === 1}
            defaultValue={
                filterState.quantityFilterState.quantity > 0 ? filterState.quantityFilterState.quantity : quantity
            }
            value={quantity}
            data-testid="quantity-select"
            onChange={onSelectChange}
        />
    )

    const verifyAvailabilityAndCheckout: MouseEventHandler<HTMLAnchorElement | HTMLButtonElement> = useCallback(
        async (e) => {
            e.preventDefault()

            const refetchResponse = await refetch()
            const target = e.target as HTMLAnchorElement

            if (refetchResponse?.data?.listing && target.href) {
                // Can't use router.push here because it breaks Checkout CSS in prod mode
                location.href = target.href
            } else {
                toggleDetails({ listing: null })
                router.push({ pathname, query: { slug, id, notix: 'true' } }, undefined, { shallow: true })
            }
        },
        [id, pathname, refetch, slug, toggleDetails],
    )

    const handleCheckout: MouseEventHandler<HTMLAnchorElement | HTMLButtonElement> = (e) => {
        optimizely?.track(OPTIMIZELY_EVENT.checkoutButtonClick)
        verifyAvailabilityAndCheckout(e)
    }

    const ticketPriceToDisplay = useMemo(() => {
        let totalPrice = ticketPrice

        if (isAllInPricing && aipFee) {
            totalPrice = aipFee
        }

        return totalPrice
    }, [isAllInPricing, ticketPrice, aipFee])

    const discountPrice = usePromoPrice(ticketPriceToDisplay, quantity)
    const hasDiscountPrice = Boolean(discountPrice)

    const getFormattedPrice = (price: number) => {
        if (applyCurrencyConversion) {
            return getFormattedCurrency(i18n, Math.ceil(price), i18nListingData?.to, applyCurrencyConversion, 0)
        }
        return isAllInPricing ? currencyFormatter.format(price) : `$${price.toLocaleString()}` // this is intentional, as per product input something to do with showing fees which may have the cents and thus keeping the format throughout
    }

    const getFormattedDiscount = (discount: number) => {
        if (applyCurrencyConversion) {
            return getFormattedCurrency(i18n, Math.ceil(discount), i18nListingData?.to, applyCurrencyConversion, 0)
        }
        return isAllInPricing
            ? currencyFormatter.format(discount)
            : `$${Number.isSafeInteger(discount) ? discount : discount?.toFixed(2)}`
    }

    const priceAmountClassName = classNames(styles.priceAmount, {
        [styles.beforeDiscount]: hasDiscountPrice,
    })

    const ticketFeeText = useMemo(() => {
        const formattedServiceFee = getFormattedCurrency(i18n, serviceFee, i18nListingData?.to, applyCurrencyConversion)
        const formattedServiceAndDeliveryFee = aipFee
            ? getFormattedCurrency(i18n, aipFee - ticketPrice, i18nListingData?.to, applyCurrencyConversion)
            : ''

        if (isAllInPricingByVenueStateFeatureEnabled) {
            return `Includes ${formattedServiceFee} in fees`
        } else if (isTennesseeAllInPricingEnabled || isConnecticutAllInPricingEnabled) {
            if (deliveryMethod?.aipShouldIncludeDelivery) {
                return `Includes ${formattedServiceAndDeliveryFee} in service and delivery fees`
            }
            return `Includes ${formattedServiceFee} in service fees`
        }
        return null
    }, [
        i18n,
        serviceFee,
        i18nListingData?.to,
        applyCurrencyConversion,
        aipFee,
        ticketPrice,
        isAllInPricingByVenueStateFeatureEnabled,
        isTennesseeAllInPricingEnabled,
        isConnecticutAllInPricingEnabled,
        deliveryMethod?.aipShouldIncludeDelivery,
    ])

    const faceValuePrice = useMemo(() => {
        if (faceValue || localPrices?.faceValue) {
            const price = applyCurrencyConversion ? localPrices?.faceValue : faceValue
            const currency = applyCurrencyConversion ? i18nListingData.to : DEFAULT_CURRENCY
            if (price) {
                return getFormattedCurrency(i18n, Math.ceil(price), currency, applyCurrencyConversion, 0)
            }
        }
        return null
    }, [i18n, i18nListingData, localPrices, faceValue, applyCurrencyConversion])

    const checkoutButtonText = useMemo(() => {
        if (isAllInPricingByVenueStateFeatureEnabled) {
            return 'Go to Checkout'
        }
        return 'Checkout'
    }, [isAllInPricingByVenueStateFeatureEnabled])

    const mobileFooter = (
        <>
            <Column pb={footerPadding.bottom} pt={footerPadding.top}>
                {qtyDropdown}
            </Column>

            <Column pb={footerPadding.bottom} pt={footerPadding.top} className={styles.alignRight}>
                <ButtonV2
                    size="large"
                    className={styles.button}
                    data-testid="mobile-checkout-button"
                    onClick={handleCheckout}
                    href={checkoutHref}
                >
                    {checkoutButtonText}
                </ButtonV2>
            </Column>
        </>
    )

    return (
        <Card squareTop squareBottom className={styles.card}>
            <TicketInfoPromoBanner />
            <CardBody className={styles.cardBody}>
                <Row className={styles.basicInfoRow} align="start">
                    {/* Left Column */}
                    <Column className={styles.basicInfoColumn}>
                        {listing?.badges?.[0] && (
                            <Row>
                                <ListingBadges badge={listing?.badges[0]} />
                            </Row>
                        )}
                        <Row>
                            <Text altFont size="xl" weight="bold" height="compressed" data-testid="section-name">
                                {listing.inGroup ? sectionLong : section}
                            </Text>
                        </Row>
                        <Row align="center">
                            {!listing.inGroup && (
                                <Text altFont as="span" size="md" data-testid="row-name">
                                    {`Row ${row}`}
                                </Text>
                            )}
                            {isPreferredSeller && (
                                <>
                                    {!listing.inGroup && (
                                        <Text
                                            altFont
                                            as="span"
                                            state="muted"
                                            size="sm"
                                            className={styles.superSellerIconDivider}
                                        >
                                            {String.fromCharCode(124)}
                                        </Text>
                                    )}
                                    <Icon type="ticket-super-seller" size="md" className={styles.superSellerIcon} />
                                    <Text altFont as="span" size="md">
                                        Super Seller
                                    </Text>
                                </>
                            )}
                        </Row>
                        {showAllTicketsInSection && (
                            <Row align="baseline" className={styles.seeAllTickets}>
                                <Link
                                    className={styles.seeAllTicketsLink}
                                    href="#"
                                    importance="link-primary-alt"
                                    data-testid="see-all-tickets-in-section-link"
                                    onClick={() => {
                                        toggleDetails({ listing: null })
                                        onSectionSelectAction({ id: sectionId, hasSectionBeenManuallyFiltered: false })
                                    }}
                                >
                                    See all tickets in this section
                                </Link>
                            </Row>
                        )}
                    </Column>
                    {/* Right Column */}
                    <Column className={styles.basicInfoColumn}>
                        {isGreatDeal && (
                            <Row align="end" justify="right">
                                <Pill importance="success" size="lg" data-testid="great-deal-badge">
                                    Great Deal
                                </Pill>
                            </Row>
                        )}
                        <Row>
                            {showAllInPricing && (
                                <Column className={classNames(styles.alignRight, styles.showAipText)}>
                                    <Text altFont size="xs" state="muted" height="compressed">
                                        Estimated fees included
                                    </Text>
                                </Column>
                            )}
                        </Row>
                        <Row className={styles.price}>
                            <Text
                                altFont
                                size="xl"
                                as="span"
                                data-testid="ticket-price"
                                className={priceAmountClassName}
                            >
                                {getFormattedPrice(ticketPriceToDisplay)}
                            </Text>
                            {hasDiscountPrice && discountPrice && (
                                <Text
                                    size="lg"
                                    as="span"
                                    className={styles.discountAmount}
                                    altFont
                                    data-testid="discount-price"
                                >
                                    {getFormattedDiscount(discountPrice)}
                                </Text>
                            )}
                            <Text altFont size="sm" as="span" className={styles.priceStyle}>
                                {!applyCurrencyConversion && !isUSVenue ? <>&nbsp;USD</> : null}&nbsp;ea
                            </Text>
                        </Row>
                        <Row justify="right">
                            {isAllInPricing && (
                                <Text altFont size="sm" state="muted" data-testid="ticket-fee-text" alignment="right">
                                    {ticketFeeText}
                                </Text>
                            )}
                        </Row>
                    </Column>
                </Row>
                {showFaceValueForUK && (
                    <FaceValue
                        faceValue={faceValue ? getFormattedCurrency(i18n, faceValue, DEFAULT_CURRENCY, true) : null}
                        className={styles.faceValueSection}
                    />
                )}
                {showSellerInfo && (
                    <Text altFont size="sm" state="muted" data-testid="seller-info" className={styles.sellerInfo}>
                        The seller of the tickets is a trader.
                    </Text>
                )}
                <Row className={styles.desktopButtons} data-testid="desktop-checkout-btn">
                    <Column pt={0.75}>{qtyDropdown}</Column>
                    <Column pt={0.75} className={styles.alignRight}>
                        <ButtonV2
                            size="large"
                            className={styles.button}
                            data-testid="desktop-checkout-button"
                            onClick={handleCheckout}
                            href={checkoutHref}
                        >
                            {checkoutButtonText}
                        </ButtonV2>
                    </Column>
                </Row>

                {isSuperBowlProduction && <BuyerGuaranteeBox />}

                {isPayLaterEnabled ? (
                    <Box
                        className={styles.paypalMessages}
                        desktopMarginTop={4}
                        desktopMarginBottom={4}
                        mobileMarginTop={4}
                        mobileMarginBottom={4}
                        paddingTop={3}
                        paddingLeft={4}
                    >
                        <PaypalProvider>
                            <PaypalMessagesWrapper />
                        </PaypalProvider>
                    </Box>
                ) : (
                    <Divider vertical={false} />
                )}

                {mobileHasSecondaryMarketplaceDisclaimer && (
                    <Box
                        mobileDisplay="flex"
                        tabletDisplay="flex"
                        desktopDisplay="none"
                        marginBottom={4}
                        data-testid="secondary-marketplace-disclaimer"
                    >
                        <Text altFont size="md" opaque>
                            {SECONDARY_MARKETPLACE_DISCLAIMER_TEXT}
                        </Text>
                    </Box>
                )}

                {shouldShowDeliveryDate && (
                    <Row data-testid="estimated-delivery">
                        <Icon type="calendar" size="md" className={styles.icon} />
                        <Column pb={1}>
                            <Text
                                altFont
                                weight="bold"
                                size="md"
                                className={styles.sectionTitle}
                                data-testid="estimated-delivery-date"
                            >
                                {dayjs(inHandDate).tz(production?.venue?.timezone).format('ddd, MMM D')}
                            </Text>
                            <Text altFont size="sm">
                                Your tickets will arrive by or before this date.
                            </Text>
                        </Column>
                    </Row>
                )}

                <Row>
                    <Icon type="calendar-multi" size="md" className={styles.icon} />
                    <Column pb={1}>
                        <Text altFont weight="bold" size="md" className={styles.sectionTitle}>
                            {productionName}
                        </Text>
                        <Text altFont size="sm">
                            {productionDateTime}
                        </Text>
                    </Column>
                </Row>

                <Row>
                    <Icon type="location" size="md" className={styles.icon} />
                    <Column>
                        <Text altFont weight="bold" size="md" className={styles.sectionTitle}>
                            {venueName}
                        </Text>
                        <Text altFont size="sm">
                            {venueAddress}
                        </Text>
                    </Column>
                </Row>

                {deliveryMethod && (
                    <Column>
                        <TicketInfoSectionHeader title="Delivery" />
                        <Row>
                            <Icon type={deliveryMethod.icon as IconType} size="md" className={styles.icon} />
                            <Column>
                                <Text
                                    altFont
                                    weight="bold"
                                    size="md"
                                    className={styles.sectionTitle}
                                    data-testid="delivery-method"
                                >
                                    {deliveryMethod.name}
                                </Text>
                                {isInstantDelivery && (
                                    <Badge className={styles.instantDelivery}>
                                        <Icon
                                            type="ticket-instant-circle"
                                            size="xs"
                                            className={styles.iconInstantDelivery}
                                        />
                                        <Text altFont weight="bold" size="md" as={'label'}>
                                            Instant Delivery
                                        </Text>
                                    </Badge>
                                )}
                                <Text altFont size="sm">
                                    {deliveryMethod.description}
                                </Text>
                            </Column>
                        </Row>
                    </Column>
                )}

                {((description && description.trim().length > 0) || isFaceValue) && (
                    <Column>
                        <TicketInfoSectionHeader title="Notes" />
                        <Row>
                            <Column pb={0.25}>
                                {description && description.trim().length > 0 && (
                                    <Text
                                        altFont
                                        size="sm"
                                        state="muted"
                                        className={styles.notesSectionParagraph}
                                        data-testid="ticket-notes"
                                    >
                                        {description}
                                    </Text>
                                )}
                                {isFaceValue && !showFaceValueForUK && (
                                    <FaceValue faceValue={faceValuePrice} className={styles.notesSectionParagraph} />
                                )}
                            </Column>
                        </Row>
                    </Column>
                )}
            </CardBody>
            <CardFooter borderTop justify="between" className={styles.footer}>
                {mobileFooter}
            </CardFooter>
        </Card>
    )
}

export default withOptimizely(TicketInformation as FC<TicketInformationProps & WithOptimizelyProps>)
